{
    "$schema": "https://json-schema.org/draft/2020-12/schema",
    "$id": "https://augmented.team/schemas/integration-metadata.v1.json",
    "title": "Integration Definition Metadata (v1)",
    "description": "Shape of integration_definitions.metadata. Carries the per-scope runtime support declaration (ENG-4924) and the auto-loaded MCP tool list (ENG-4925).",
    "type": "object",
    "additionalProperties": true,
    "properties": {
        "base_url": {
            "type": "string",
            "format": "uri",
            "pattern": "^https://",
            "description": "Absolute HTTPS URL the broker uses as the vendor API root. Required when any tool descriptor relies on http_templater (i.e. whenever `tools[]` is non-empty)."
        },
        "auth_scheme": {
            "type": "string",
            "pattern": "^[A-Za-z0-9!#$%&'*+.^_`|~-]+$",
            "description": "Authorization scheme word placed before the credential, e.g. `Key` for Higgsfield's `Authorization: Key KEY_ID:KEY_SECRET` (ENG-8440). Omit for the `Bearer` default. Constrained to a single RFC 7230 token because it is concatenated straight into a header value."
        },
        "auth_encoding": {
            "type": "string",
            "enum": ["basic_username"],
            "description": "How the credential is encoded into Authorization (ENG-10524). `basic_username` sends it as the HTTP Basic username with an empty password (`Authorization: Basic base64(<credential>:)`), Sportsyear's API-key form. Omit for `<auth_scheme> <credential>`. Must not be combined with `auth_scheme`."
        },
        "runtime_scopes": {
            "type": "object",
            "description": "Per-runtime-scope support map. Each slot is either null (the integration does not support installs at this scope) or an object describing how token resolution and auth work for that scope.",
            "additionalProperties": false,
            "properties": {
                "org": { "$ref": "#/$defs/scopeConfig" },
                "team": { "$ref": "#/$defs/scopeConfig" },
                "agent": { "$ref": "#/$defs/scopeConfig" }
            }
        },
        "action_item_target": {
            "type": "object",
            "description": "Declares that this integration is somewhere action items can be FILED (ENG-9151). Presence is the signal; the object carries display copy. Absent means the integration is not offered as an action-item destination. Deliberately a declaration rather than something inferred from `category` or from tool names \u2014 see the resolver header in packages/api/src/lib/notes-action-item-targets.ts for why both inferences are wrong.",
            "additionalProperties": true,
            "properties": {
                "label": {
                    "type": "string",
                    "minLength": 1,
                    "description": "Short product noun as it should read mid-sentence: \"create action items in Linear\". Falls back to display_name when absent, which is why it exists \u2014 several definitions are named \"... Pack\"."
                }
            }
        },
        "tools": {
            "type": "array",
            "description": "Auto-loaded MCP tool descriptors. Each entry produces one MCP tool entry per supported runtime scope.",
            "items": { "$ref": "#/$defs/toolDescriptor" }
        }
    },
    "if": {
        "type": "object",
        "properties": { "tools": { "type": "array", "minItems": 1 } },
        "required": ["tools"]
    },
    "then": { "required": ["base_url"] },
    "not": {
        "description": "auth_scheme and auth_encoding are mutually exclusive (ENG-10524). The templater refuses the combination on every call, so catalog validation refuses it first.",
        "required": ["auth_scheme", "auth_encoding"]
    },
    "$defs": {
        "scopeConfig": {
            "oneOf": [
                { "type": "null" },
                {
                    "type": "object",
                    "additionalProperties": true,
                    "required": ["auth", "token_holder"],
                    "properties": {
                        "auth": {
                            "type": "string",
                            "minLength": 1,
                            "description": "Auth scheme identifier (e.g. oauth2_workspace, oauth2_user, oauth2_tenant, api_key)."
                        },
                        "token_holder": {
                            "type": "string",
                            "enum": ["broker", "agent"],
                            "description": "Who holds the credential at runtime. `broker` = central vault (org/team installs typically); `agent` = the agent runtime resolves its own token (existing managed-toolkits path)."
                        },
                        "oauth_scopes": {
                            "type": "array",
                            "items": { "type": "string", "minLength": 1 },
                            "description": "Optional OAuth scope strings for this scope tier. May differ between org-level and per-user installs (e.g. workspace vs user scope)."
                        }
                    }
                }
            ]
        },
        "toolDescriptor": {
            "type": "object",
            "additionalProperties": true,
            "required": ["name", "description", "risk_tier", "input_schema", "http"],
            "properties": {
                "name": {
                    "type": "string",
                    "minLength": 1,
                    "pattern": "^[a-z][a-z0-9_]*(\\.[a-z][a-z0-9_]*)*$",
                    "description": "Dotted lowercase tool name within the integration (e.g. `invoices.create`). Combined with the integration code_name to form the MCP tool name (e.g. `xero.invoices.create`)."
                },
                "description": {
                    "type": "string",
                    "minLength": 1,
                    "description": "Human-readable description of what this tool does. Used as the MCP tool description AND as the action verb on the approval card."
                },
                "risk_tier": {
                    "type": "string",
                    "enum": ["Low", "Medium", "High"],
                    "description": "Drives approval routing. Combined with the agent's CHARTER policy in the dispatcher to produce auto_approve / route_to_approver / hard_deny. Reads should generally be Low; writes Medium; destructive or financial High."
                },
                "input_schema": {
                    "type": "object",
                    "description": "JSON Schema for the tool's input arguments. Used verbatim as the MCP tool's `inputSchema` AND as the source for the approval card's field rendering. Should be `{ type: 'object', properties: ..., required?: ... }`.",
                    "required": ["type", "properties"],
                    "properties": {
                        "type": { "const": "object" },
                        "properties": { "type": "object" },
                        "required": { "type": "array", "items": { "type": "string" } }
                    }
                },
                "http": {
                    "type": "object",
                    "additionalProperties": false,
                    "required": ["method", "path_template"],
                    "properties": {
                        "method": {
                            "type": "string",
                            "enum": ["GET", "POST", "PUT", "PATCH", "DELETE"]
                        },
                        "path_template": {
                            "type": "string",
                            "minLength": 1,
                            "description": "URL path with `{arg}` placeholders bound to validated input fields (e.g. `/api.xro/2.0/Invoices/{invoice_id}`)."
                        },
                        "body_template": {
                            "description": "Optional body shape with `{arg}` placeholders. JSON-serialised at request time; pass-through fields can be referenced as `{$body}` to inject the entire input."
                        },
                        "query_template": {
                            "type": "object",
                            "description": "Optional query-string shape with `{arg}` placeholders.",
                            "additionalProperties": { "type": "string" }
                        },
                        "headers_template": {
                            "type": "object",
                            "description": "Optional request-header shape with `{arg}` placeholders (e.g. `{ \"x-origami-project\": \"{project_id}\" }`). A header whose template resolves to empty is dropped rather than sent blank, and args consumed only by a header template are excluded from a `{$body}` expansion.",
                            "propertyNames": { "pattern": "^[A-Za-z0-9!#$%&'*+.^_`|~-]+$" },
                            "additionalProperties": { "type": "string" }
                        },
                        "idempotency_key_header": {
                            "type": "string",
                            "minLength": 1,
                            "pattern": "^[A-Za-z0-9-]+$",
                            "description": "Optional override for the idempotency-key header name. Defaults to `Idempotency-Key`. Constrained to RFC 7230 token characters (letters, digits, hyphen) to reject empty/invalid header names at write time."
                        }
                    }
                },
                "applicable_scopes": {
                    "type": "array",
                    "items": { "type": "string", "enum": ["org", "team", "agent"] },
                    "description": "Optional subset of the integration's runtime_scopes this tool is exposed under. Default: all scopes the integration declares."
                },
                "auth_mode": {
                    "type": "string",
                    "enum": ["required", "optional"],
                    "description": "ADR 0010 — per-tool credential expectation. `required` (default) makes the broker fail closed when no install credential is attached; `optional` lets the broker call the vendor without an `Authorization` header (used by keyless integrations such as Augmented Live)."
                }
            }
        }
    }
}
